import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, ArrowRight, Loader2, CheckCircle2, Smartphone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { authApi, saveAuth } from '../lib/api';
import EmailOtpFlow from '../components/EmailOtpFlow';

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('phone'); // 'phone' | 'email'
  const [form, setForm] = useState({ name: '', phone: '', company: '', role: 'fleet', city: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, user } = await authApi.signup(form);
      saveAuth(token, user);
      toast({ title: 'Welcome to EZLOAD!', description: '1 month free trial activated.' });
      navigate('/app/dashboard');
    } catch (err) {
      toast({ title: 'Signup failed', description: err?.response?.data?.detail || 'Try a different phone number' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-[#0B2545] p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#00A651]/20 rounded-full blur-3xl"></div>
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <div className="w-10 h-10 rounded-lg brand-gradient flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-2xl text-white font-[Poppins]">EZLOAD</span>
        </Link>
        <div className="relative z-10 space-y-5">
          <h2 className="text-4xl font-extrabold text-white leading-tight font-[Poppins]">Start your 1-month free trial.</h2>
          {['Full Load & Truck Board', 'GST-compliant e-LR & e-Way Bills', 'UPI, RTGS, COD payments', 'No credit card required'].map((b, i) => (
            <div key={i} className="flex items-center gap-3 text-white/90">
              <div className="w-6 h-6 rounded-full bg-[#00A651]/20 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-[#00A651]" />
              </div>
              <span>{b}</span>
            </div>
          ))}
        </div>
        <div className="relative z-10 text-xs text-white/40">© 2025 EZLOAD Technologies Pvt. Ltd.</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-xl text-[#0B2545] font-[Poppins]">EZLOAD</span>
          </Link>
          <h1 className="text-3xl font-extrabold text-[#0B2545] font-[Poppins] mb-2">Create your account</h1>
          <p className="text-gray-500 mb-6">Free for 1 month. ₹500 + GST/month after.</p>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              data-testid="signup-tab-phone"
              type="button"
              onClick={() => setTab('phone')}
              className={`h-9 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'phone' ? 'bg-white shadow text-[#0B2545]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Smartphone className="w-4 h-4" /> Phone + Password
            </button>
            <button
              data-testid="signup-tab-email"
              type="button"
              onClick={() => setTab('email')}
              className={`h-9 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'email' ? 'bg-white shadow text-[#0B2545]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Mail className="w-4 h-4" /> Email OTP
            </button>
          </div>

          {tab === 'phone' ? (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Full Name</Label>
                  <Input data-testid="signup-name-input" required placeholder="Rajesh Kumar" className="h-11 mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Mobile</Label>
                  <Input data-testid="signup-phone-input" required type="tel" placeholder="98765 43210" className="h-11 mt-1.5" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Company / Fleet Name</Label>
                <Input data-testid="signup-company-input" required placeholder="Kumar Transports Pvt. Ltd." className="h-11 mt-1.5" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="signup-role-select" className="h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fleet">Fleet Owner</SelectItem>
                      <SelectItem value="shipper">Shipper</SelectItem>
                      <SelectItem value="broker">Broker / Agent</SelectItem>
                      <SelectItem value="driver">Driver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">City</Label>
                  <Input data-testid="signup-city-input" required placeholder="Mumbai" className="h-11 mt-1.5" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Password</Label>
                <Input data-testid="signup-password-input" required type="password" placeholder="Min 6 characters" className="h-11 mt-1.5" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
              <Button data-testid="signup-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#00A651] hover:bg-[#008a43] text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <>Start Free Trial <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              <p className="text-[11px] text-gray-500 text-center leading-relaxed">By signing up you agree to EZLOAD's Terms of Service and Privacy Policy.</p>
            </form>
          ) : (
            <EmailOtpFlow mode="signup" navigateTo="/app/dashboard" />
          )}

          <p className="text-sm text-center text-gray-500 mt-4">
            Already have an account? <Link to="/login" className="text-[#00A651] font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
